import { BaseApi } from "@/api/BaseApi";
import { baseUrl as confBaseUrl, itemsPerPage } from "@/config/globals";
import type { AxiosResponse } from "axios";
import axios from "axios";
import { ApplicationError } from "@/application/ApplicationError";
import type { EducationInterface } from "@/application/EducationInterface";

class ApplicationEducationApi extends BaseApi {
    private appId: number | null = null;

    protected getBaseCollectionUrl(): string {
        if (!this.appId) {
            throw new ApplicationError(
                "Trying to generate url to education collection when appId is not set."
            );
        }
        return super.buildItemUrl(this.appId) + "/application-education";
    }

    protected buildItemUrl(id: number): string {
        return this.getBaseCollectionUrl() + "/" + id;
    }

    private toRequestBody(
        education: EducationInterface
    ): Partial<EducationInterface> {
        const body: Partial<EducationInterface> = { ...education };
        // Legacy free-text degree is never sent from the form (neither null nor "").
        delete body.degree;
        // Read-only nested object from the API response.
        delete body.exit_qualification;
        // Always number | null, never ""/undefined.
        body.exit_qualification_id = education.exit_qualification_id ?? null;
        return body;
    }

    public async createEducation(
        education: EducationInterface
    ): Promise<EducationInterface> {
        this.appId = education.application_id;
        const url = this.buildCollectionUrl(null);
        this.appId = null;
        const response: AxiosResponse<EducationInterface> = await axios.post(
            url,
            this.toRequestBody(education),
            {
                headers: await this.decorateHeadersWithAuthorization({}),
            }
        );
        return response.data;
    }

    public async updateEducation(
        education: EducationInterface
    ): Promise<EducationInterface> {
        this.appId = education.application_id;
        const url = this.buildItemUrl(education.id as number);
        this.appId = null;
        const response: AxiosResponse<EducationInterface> = await axios.patch(
            url,
            this.toRequestBody(education),
            {
                headers: await this.decorateHeadersWithAuthorization({}),
            }
        );
        return response.data;
    }

    public async deleteEducation(id: number, appId: number): Promise<void> {
        this.appId = appId;
        const url = this.buildItemUrl(id as number);
        this.appId = null;
        await axios.delete(url, {
            headers: await this.decorateHeadersWithAuthorization({}),
        });
    }
}

const baseUrl = confBaseUrl + "/application";
export const applicationEducationApi = new ApplicationEducationApi(
    baseUrl,
    itemsPerPage
);
